import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Settings, Grid3X3, Shield, ShieldOff } from "lucide-react";
import { DeviceSidebar } from "./DeviceSidebar";
import { LevelBrowserCategoriesContent } from "./LevelBrowserCategoriesContent";
import { LevelBrowserWhitelistContent } from "./LevelBrowserWhitelistContent";
import { LevelBrowserBlacklistContent } from "./LevelBrowserBlacklistContent";
import { LevelBrowserSettingsContent } from "./LevelBrowserSettingsContent";
import { useLevelBrowserPolicies } from "@/hooks/useLevelBrowserPolicies";
import { useLevelBrowserMode } from "@/hooks/useLevelBrowserMode";
import { type NsfwPresetLevel, type AccuracyLevel, DEFAULT_BLUEJAY_CONFIG } from "@/config/nsfwRulePresets";
import type { BrowserMode } from "@/types/convex";

interface LevelBrowserTabProps {
  levelId: string;
}

export const LevelBrowserTab = ({ levelId }: LevelBrowserTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("settings");
  const [nsfwLevel, setNsfwLevel] = useState<NsfwPresetLevel>(DEFAULT_BLUEJAY_CONFIG.nsfwLevel);
  const [human, setHuman] = useState<boolean>(DEFAULT_BLUEJAY_CONFIG.human);
  const [accuracy, setAccuracy] = useState<AccuracyLevel>(DEFAULT_BLUEJAY_CONFIG.accuracy);

  // Use the new mode hook for explicit mode management
  const { browserMode, setBrowserMode, isLoading: isModeLoading } = useLevelBrowserMode(levelId);

  // Get level browser policies for updating settings (not mode)
  const { browserPolicy, isLoading, updateBrowserPolicy } = useLevelBrowserPolicies(levelId);

  // Restore active section from URL params
  useEffect(() => {
    const section = searchParams.get('browserSection');
    if (section && ['categories', 'whitelist', 'blacklist', 'settings'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Initialize config state from browserPolicy
  useEffect(() => {
    if (isLoading || !browserPolicy) return;
    
    setNsfwLevel(browserPolicy.nsfwLevel || DEFAULT_BLUEJAY_CONFIG.nsfwLevel);
    setHuman(browserPolicy.human || DEFAULT_BLUEJAY_CONFIG.human);
    setAccuracy(browserPolicy.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy);
  }, [browserPolicy, isLoading]);

  /**
   * Filter sidebar items based on explicit browserMode:
   * - none: Settings only
   * - whitelist: Settings, Whitelist only
   * - categories: All sections
   */
  const sidebarItems = [
    { id: "settings", label: "Settings", icon: Settings },
    { id: "categories", label: "Categories", icon: Grid3X3 },
    { id: "whitelist", label: "White List", icon: Shield },
    { id: "blacklist", label: "Black List", icon: ShieldOff },
  ];

  const filteredSidebarItems = 
    browserMode === "none" 
      ? sidebarItems.filter(item => item.id === "settings")
      : browserMode === "whitelist"
      ? sidebarItems.filter(item => ["settings", "whitelist"].includes(item.id))
      : sidebarItems; // categories mode - show all

  // Switch to appropriate section if current section is not available for the mode
  useEffect(() => {
    const availableIds = filteredSidebarItems.map(item => item.id);
    if (!availableIds.includes(activeSection)) {
      setActiveSection(filteredSidebarItems[0]?.id || "settings");
    }
  }, [browserMode, activeSection, filteredSidebarItems]);

  // Save active section to URL params
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('browserSection', section);
    setSearchParams(newSearchParams);
  };

  // Handle browser mode change - uses the new hook
  const handleBrowserModeChange = async (mode: BrowserMode) => {
    await setBrowserMode(mode);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "settings":
        return (
          <LevelBrowserSettingsContent
            levelId={levelId}
            browserMode={browserMode === "categories" ? "filtered" : browserMode}
            onBrowserModeChange={(mode) => {
              // Map "filtered" back to "categories" for the hook
              const mappedMode: BrowserMode = mode === "filtered" ? "categories" : mode;
              handleBrowserModeChange(mappedMode);
            }}
            nsfwLevel={nsfwLevel}
            onNsfwLevelChange={(level) => {
              setNsfwLevel(level);
              updateBrowserPolicy({ nsfwLevel: level });
            }}
            human={human}
            onHumanChange={(value) => {
              setHuman(value);
              updateBrowserPolicy({ human: value });
            }}
            accuracy={accuracy}
            onAccuracyChange={(value) => {
              setAccuracy(value);
              updateBrowserPolicy({ accuracy: value });
            }}
            isLoading={isLoading}
          />
        );
      case "categories":
        return <LevelBrowserCategoriesContent levelId={levelId} />;
      case "whitelist":
        return <LevelBrowserWhitelistContent levelId={levelId} />;
      case "blacklist":
        return <LevelBrowserBlacklistContent levelId={levelId} />;
      default:
        return (
          <LevelBrowserSettingsContent
            levelId={levelId}
            browserMode={browserMode === "categories" ? "filtered" : browserMode}
            onBrowserModeChange={(mode) => {
              const mappedMode: BrowserMode = mode === "filtered" ? "categories" : mode;
              handleBrowserModeChange(mappedMode);
            }}
            nsfwLevel={nsfwLevel}
            onNsfwLevelChange={(level) => {
              setNsfwLevel(level);
              updateBrowserPolicy({ nsfwLevel: level });
            }}
            human={human}
            onHumanChange={(value) => {
              setHuman(value);
              updateBrowserPolicy({ human: value });
            }}
            accuracy={accuracy}
            onAccuracyChange={(value) => {
              setAccuracy(value);
              updateBrowserPolicy({ accuracy: value });
            }}
            isLoading={isLoading}
          />
        );
    }
  };

  return (
    <div className="flex h-full">
      <DeviceSidebar
        title="Browser Management"
        items={filteredSidebarItems}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
      />
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
